import React, { memo, useCallback, useState, useMemo } from 'react';
import { formatCurrency } from '../../utilities/general-utils';
import { useSnowballContext } from '../../contexts/snowball-context';
import PaymentTypeSelect from './PaymentTypeSelect';
import FixedPaymentInput from './FixedPaymentInput';
import ExtraPayments from './ExtraPayments';
import PaymentSchedule from '../PaymentSchedule';
import AccountSummary from './AccountSummary';

const SnowballAccount = memo(({ 
    account, 
    onAccountUpdate,
    showSchedule = true,
    showSummary = true,
    isDisabled = false 
}) => {
    const { masterStartDelayMonths } = useSnowballContext();
    
    const [localState, setLocalState] = useState({
        paymentType: (account.fixed_payment && account.fixed_payment > 0) ? 'fixed' : 'minimum',
        fixedPayment: account.fixed_payment || 0,
        extraPayments: account.extraPayments || [],
        startDateDelayMonths: account.startDateDelayMonths ?? 0,
        hasCustomDelay: account.startDateDelayMonths !== undefined && account.startDateDelayMonths !== null
    });

    // Calculate the effective display value (what should be shown in the input)
    const effectiveStartDelayDisplay = useMemo(() => {
        if (localState.hasCustomDelay) {
            // Account has been manually set - show the explicit value
            return localState.startDateDelayMonths;
        } else {
            // Account is using master delay - show the master value for display
            return masterStartDelayMonths ?? 0;
        }
    }, [localState.hasCustomDelay, localState.startDateDelayMonths, masterStartDelayMonths]);

    const hasChanges = useMemo(() => {
        const currentPaymentType = (account.fixed_payment && account.fixed_payment > 0) ? 'fixed' : 'minimum';
        const accountHasCustomDelay = account.startDateDelayMonths !== undefined && account.startDateDelayMonths !== null;
        
        return (
            localState.paymentType !== currentPaymentType ||
            localState.fixedPayment !== (account.fixed_payment || 0) ||
            JSON.stringify(localState.extraPayments) !== JSON.stringify(account.extraPayments || []) ||
            localState.hasCustomDelay !== accountHasCustomDelay ||
            (localState.hasCustomDelay && localState.startDateDelayMonths !== (account.startDateDelayMonths ?? 0))
        );
    }, [localState, account]);

    const handlePaymentTypeChange = useCallback((paymentType) => {
        setLocalState(prev => ({
            ...prev,
            paymentType: paymentType.target.value
        }));
    }, []);

    const handleFixedPaymentChange = useCallback((value) => {
        const numericValue = parseFloat(value) || 0;
        setLocalState(prev => ({
            ...prev,
            fixedPayment: numericValue
        }));
    }, []);

    const handleExtraPaymentsChange = useCallback((extraPayments) => {
        setLocalState(prev => ({
            ...prev,
            extraPayments
        }));
    }, []);

    const handleStartDateDelayChange = useCallback((delayMonths) => {
        const months = parseInt(delayMonths) || 0;
        const clampedMonths = Math.min(Math.max(months, 0), 48);
        setLocalState(prev => ({
            ...prev,
            startDateDelayMonths: clampedMonths,
            hasCustomDelay: true // Mark as custom when user makes any change
        }));
    }, []);

    const handleApplyUpdates = useCallback((e) => {
        e.preventDefault();
        
        if (!hasChanges) {
            return;
        }

        const updatedAccount = {
            ...account,
            paymentType: localState.paymentType,
            fixed_payment: localState.fixedPayment,
            extraPayments: localState.extraPayments,
            // Only set startDateDelayMonths if the user has customized it
            ...(localState.hasCustomDelay && { startDateDelayMonths: localState.startDateDelayMonths })
        };

        onAccountUpdate(updatedAccount);
    }, [account, localState, hasChanges, onAccountUpdate]);

    const handleReset = useCallback(() => {
        setLocalState({
            paymentType: (account.fixed_payment && account.fixed_payment > 0) ? 'fixed' : 'minimum',
            fixedPayment: account.fixed_payment || 0,
            extraPayments: account.extraPayments || [],
            startDateDelayMonths: account.startDateDelayMonths ?? 0,
            hasCustomDelay: account.startDateDelayMonths !== undefined && account.startDateDelayMonths !== null
        });
    }, [account]);

    const fixedPaymentValidation = useMemo(() => {
        if (localState.paymentType === 'fixed') {
            const minRequired = account.minFixedAmount || 0;
            if (localState.fixedPayment < minRequired) {
                return {
                    isValid: false,
                    message: `Fixed payment must be at least ${formatCurrency(minRequired)}`
                };
            }
        }
        return { isValid: true, message: '' };
    }, [localState.paymentType, localState.fixedPayment, account.minFixedAmount]);

    return (
        <div style={{
            margin: "0 auto 2rem",
            maxWidth: "1000px",
            backgroundColor: "#fff",
            padding: "1.5rem",
            border: "1px solid #ddd",
            borderRadius: "8px",
            position: "relative",
            opacity: isDisabled ? 0.7 : 1
        }}>
            {/* Account Index Badge */}
            <div style={{
                position: 'absolute',
                right: '1rem',
                top: '1rem',
                backgroundColor: '#007bff',
                color: 'white',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.9rem'
            }}>
                {account.index + 1}
            </div>

            {/* Account Header */}
            <div style={{ marginBottom: '1.5rem', paddingRight: '3rem' }}>
                <h3 style={{ 
                    margin: '0 0 0.5rem 0', 
                    color: '#333',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    {account.name}
                </h3>
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    fontSize: '0.9rem',
                    color: '#666'
                }}>
                    <span><strong>Balance:</strong> {formatCurrency(account.balance)}</span>
                    <span><strong>APR:</strong> {(account.apr * 100).toFixed(2)}%</span>
                    {account.paymentSchedule && account.paymentSchedule.length > 0 && (
                        <>
                            <span><strong>Payoff Time:</strong> {account.paymentSchedule.length} payments</span>
                            <span><strong>({(account.paymentSchedule.length / 12).toFixed(1)} years)</strong></span>
                        </>
                    )}
                </div>
            </div>

            {/* Account Summary */}
            {showSummary && <AccountSummary account={account} />}

            {/* Payment Configuration */}
            <div style={{ 
                marginBottom: '2rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px'
            }}>
                <h4 style={{ margin: '0 0 1rem 0' }}>Payment Configuration</h4>
                
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "1rem",
                    alignItems: "start",
                    marginBottom: "1rem"
                }}>
                    <PaymentTypeSelect
                        value={localState.paymentType}
                        onPaymentTypeChange={handlePaymentTypeChange}
                        disabled={isDisabled}
                    />

                    {localState.paymentType === "fixed" && (
                        <div>
                            <FixedPaymentInput
                                account={account}
                                value={localState.fixedPayment}
                                onChange={handleFixedPaymentChange}
                                disabled={isDisabled}
                            />
                            {!fixedPaymentValidation.isValid && (
                                <div style={{ 
                                    color: '#dc3545', 
                                    fontSize: '0.875rem', 
                                    marginTop: '0.25rem' 
                                }}>
                                    {fixedPaymentValidation.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Start Date Delay */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '500',
                            color: '#333'
                        }}>
                            Start Delay (Months)
                            {!localState.hasCustomDelay && masterStartDelayMonths > 0 && (
                                <span style={{
                                    marginLeft: '0.5rem',
                                    fontSize: '0.8rem',
                                    color: '#007bff',
                                    fontWeight: 'normal'
                                }}>
                                    (using master: {masterStartDelayMonths})
                                </span>
                            )}
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="48"
                            value={effectiveStartDelayDisplay}
                            onChange={(e) => handleStartDateDelayChange(e.target.value)}
                            disabled={isDisabled}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: localState.hasCustomDelay ? '1px solid #ddd' : '1px solid #007bff',
                                borderRadius: '4px',
                                fontSize: '1rem',
                                backgroundColor: localState.hasCustomDelay ? '#fff' : '#f8f9ff'
                            }}
                        />
                        <div style={{
                            fontSize: '0.8rem',
                            color: '#666',
                            marginTop: '0.25rem'
                        }}>
                            {effectiveStartDelayDisplay === 0 ? 
                                'Payments start immediately' : 
                                `Payments start ${effectiveStartDelayDisplay} month${effectiveStartDelayDisplay === 1 ? '' : 's'} later`
                            }
                            {!localState.hasCustomDelay && masterStartDelayMonths > 0 && (
                                <div style={{ color: '#007bff', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                    Inheriting from master delay. Change this value to override.
                                </div>
                            )}
                            {localState.hasCustomDelay && (
                                <div style={{ color: '#28a745', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                    Custom delay set for this account.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Extra Payments */}
                <ExtraPayments
                    extraPayments={localState.extraPayments}
                    setExtraPayments={handleExtraPaymentsChange}
                    disabled={isDisabled}
                />

                {/* Action Buttons */}
                <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    marginTop: '1rem',
                    flexWrap: 'wrap'
                }}>
                    <button
                        className={`button ${hasChanges ? 'button-blue' : 'button-gray'}`}
                        onClick={handleApplyUpdates}
                        disabled={!hasChanges || isDisabled || !fixedPaymentValidation.isValid}
                        style={{ minWidth: '140px' }}
                    >
                        {hasChanges ? 'Apply Changes' : 'No Changes'}
                    </button>

                    {hasChanges && (
                        <button
                            className="button button-outline-secondary"
                            onClick={handleReset}
                            disabled={isDisabled}
                            style={{ minWidth: '100px' }}
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {/* Payment Schedule */}
            {showSchedule && account.paymentSchedule && account.paymentSchedule.length > 0 && (
                <div>
                    <h4 style={{ margin: '0 0 1rem 0' }}>Payment Schedule</h4>
                    <PaymentSchedule data={account.paymentSchedule} />
                </div>
            )}

            {/* No Schedule Message */}
            {showSchedule && (!account.paymentSchedule || account.paymentSchedule.length === 0) && (
                <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    color: '#666'
                }}>
                    <p>Payment schedule will appear here after calculation</p>
                </div>
            )}
        </div>
    );
});

SnowballAccount.displayName = 'SnowballAccount';

export default SnowballAccount;